import { createAsyncThunk } from "@reduxjs/toolkit";

import { fetchDineinTableWithOrder } from "@/lib/api";
import {
  forgetPriorRoundLines,
  rememberPriorRoundLines,
} from "@/lib/roundTracking";
import { setResumedOrderId } from "@/store/slices/cartSlice";
import { setUpdateDineinTable } from "@/store/slices/dineinSlice";
import { setSnackbar } from "@/store/slices/snackbarSlice";
import type { RootState } from "..";

/**
 * `order/fetchDineinOrderDetails` — the table-resume lookup.
 *
 * THE WRITE-PATH GAP THIS CLOSES. The storefront had no way to learn that a
 * table already had a bill, so every round POSTed a new order: the table's
 * `order_id` was overwritten to point at the newest one and the earlier bill
 * stayed open, billable and invisible on the floor plan. Live evidence on
 * `butt-karahi-main`: `WEB_DINE-IN` #22/#23 on T5 and #2/#3 on T7, each table
 * linked only to the later id.
 *
 * It runs at BOTH of the original's call sites — on table pick and again on
 * checkout mount. The second is what makes a reload safe: component state does
 * not survive it, and a customer who reloads the checkout must not thereby open
 * a second bill.
 *
 * ## What this reconstruction deliberately does NOT do
 *
 * The original ADOPTS the whole server order into the cart — items, consumer,
 * totals — and then PUTs a client-computed field-level diff against
 * `iterations[0]`. Here the cart keeps ONLY the new round and adopts nothing but
 * the `_id`; `:4001` appends the round and re-derives every figure from the
 * catalog. The reason is that `/web/*` is a public surface: a client that
 * computes `grand_total` can choose it. The customer-visible behaviour is the
 * same — one bill per table, accumulating rounds — and the money is derived by
 * the same code that priced the first round. Recorded as a deviation.
 *
 * ## The ownership gate
 *
 * Verbatim from the original: adopt only when
 * `isLoggedIn && (isStaff || order.consumer.phone === customer.phone_number)`,
 * otherwise refuse with "Table is already occupied by another customer." A
 * customer who scans the QR code on a table someone else is sitting at must not
 * be shown, or allowed to add to, that stranger's bill.
 */
export const resumeDineinTable = createAsyncThunk<
  { resumed: boolean; blocked: boolean },
  { tableId?: string | null; tenantId?: string | null },
  { state: RootState }
>("order/fetchDineinOrderDetails", async ({ tableId, tenantId }, thunkApi) => {
  const idle = { resumed: false, blocked: false };
  if (!tableId) return idle;

  const { auth } = thunkApi.getState();
  // `tenantId` may legitimately be absent here. A QR deep link commits the table
  // from the page's own mount effect, which can run BEFORE the effect that
  // dispatches the SSR settings into the store — so `settings.data._id` is empty
  // for the first pass. Bailing out on that made the lookup silently never fire
  // and every round opened a new bill again, which is the whole defect. Fall
  // through to the fetcher's build-time `TENANT_ID` default instead; this
  // storefront serves exactly one tenant.
  const table = await fetchDineinTableWithOrder(tableId, tenantId || undefined);

  // A FAILED lookup is not an empty table. `fetchDineinTableWithOrder` answers
  // null on error, and treating that as "no bill here" is precisely how a
  // duplicate gets created — so leave whatever was already adopted in place and
  // let the checkout's own guard decide.
  if (!table) return idle;

  // THE FLOOR-PLAN REFRESH. The original splits the response and dispatches the
  // TABLE half first, unconditionally, before it looks at the order at all:
  //
  //   let { data: { order: e, ...t } } = await a.Q.get("…?with_order=true");
  //   if ((o((0, b.eV)(t)), null != e)) { … }        // b.eV = setUpdateDineinTable
  //
  // This is the storefront's only live-ish table update — it has no socket — and
  // it matters in both directions: a table the server has since FREED stops
  // rendering as occupied without waiting for the next full layout load, and a
  // table someone else has just seated starts rendering as occupied. The `order`
  // key is stripped because the reducer merges the payload onto the table.
  const { order: _order, ...tableFields } = table;
  thunkApi.dispatch(setUpdateDineinTable(tableFields));

  const order = table.order ?? null;
  if (!order) {
    // The table really is free. Release any bill this cart had adopted: it
    // belongs to a table that has since been settled or cleared.
    thunkApi.dispatch(setResumedOrderId(null));
    forgetPriorRoundLines();
    return idle;
  }

  const isStaff = auth.isStaff;
  const isLoggedIn = auth.isLoggedIn;
  const mine =
    Boolean(order.customer?.phone) &&
    Boolean(auth.customer?.phone) &&
    order.customer?.phone === auth.customer?.phone;

  // The original's gate is `isLoggedIn && (isStaff || phone matches)`. The
  // `isStaff` branch is written as a BYPASS of the phone check there, and on
  // that platform an operator is always `isLoggedIn` too — they reach the staff
  // role through Firebase auth. Here they have a second route: "Switch Staff"
  // (`switchStaffAuth`, the 6-digit access code) sets `staff`/`isStaff` and
  // deliberately does NOT touch the customer identity, so `isLoggedIn` stays
  // false. Reading the gate literally therefore refused a real operator's own
  // table with "Table is already occupied by another customer." — the reopen
  // flow, dead, for anyone who signed in by access code.
  //
  // Regrouped so `isStaff` is the bypass it is meant to be, and the CUSTOMER
  // rule is untouched: a customer still has to be signed in AND own the bill.
  if (!(isStaff || (isLoggedIn && mine))) {
    thunkApi.dispatch(setResumedOrderId(null));
    forgetPriorRoundLines();
    thunkApi.dispatch(
      setSnackbar({
        message: "Table is already occupied by another customer.",
        severity: "error",
      }),
    );
    return { resumed: false, blocked: true };
  }

  thunkApi.dispatch(setResumedOrderId(order._id));
  /**
   * THE BILL AS IT STANDS BEFORE THIS ROUND — the one moment it is ever seen.
   *
   * `?with_order=true` hands back the whole order, `toWebOrder`-shaped, so its
   * `items[].lineId`s are the lines that already existed. Recording them here
   * is what lets the confirmation receipt separate "Already Ordered" from the
   * round that was just rung in; see `lib/roundTracking.ts` for why the split
   * is done this way and not from a server field.
   */
  rememberPriorRoundLines(
    String(order._id),
    (order.items ?? []).map((line) => line.lineId),
  );
  return { resumed: true, blocked: false };
});
